import { Editor, Element, Point, Range, Transforms } from 'slate'
import { useSelected, useSlate } from 'slate-react'
import React from 'react'

import { Col, Grid } from '../../../kiti/components/structure/Grid'
import { useColor } from '../../../kiti/components/theme/_hooks/colors'
import { useEditorState, useNodeMenuForm } from '../EditorContext'
import EditorIcon from '../EditorIcon'
import EditorMenuFormWrapper from '../EditorMenuFormWrapper'
import Input from '../../../kiti/components/form/Input'

const PluginElement = ({ children, attributes, element, ...props }) => {
  const color = useColor('blue')
  const selected = useSelected()

  return (
    <a {...attributes} href={element.url} style={{ color, boxShadow: selected && '0 0 0 2px #B4D5FF' }}>
      {children}
    </a>
  )
}

const isActive = editor => {
  const [link] = Editor.nodes(editor, {
    match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link'
  })
  return !!link
}

const toggle = (editor, menu, setMenu) => {
  const active = isActive(editor)
  const { selection } = editor

  if (menu === 'link') return
  if (!selection) return

  if (active) {
    if (Point.equals(selection?.anchor, selection?.focus)) {
      const offset = Editor.leaf(editor, selection?.focus)[0]?.text?.length
      const isInverted = Point.isBefore(selection?.anchor, selection?.focus)
      const init = isInverted ? selection.focus : selection?.anchor
      const end = isInverted ? selection.anchor : selection?.focus

      Transforms.select(editor, { anchor: { ...init, offset: 0 }, focus: { ...end, offset } })
    }
  }

  setTimeout(() => setMenu('link'), 200)
}

const ToggleIcon = ({ ...props }) => {
  const { menu, setMenu } = useEditorState()
  const editor = useSlate()
  const active = isActive(editor)

  return <EditorIcon onClick={() => toggle(editor, menu, setMenu)} icon="RiLinkM" active={active} {...props} />
}

const unwrapLink = editor => {
  Transforms.unwrapNodes(editor, {
    match: n => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link'
  })
}

const wrapLink = (editor, url: string) => {
  if (isActive(editor)) {
    unwrapLink(editor)
  }

  const { selection } = editor
  const isCollapsed = selection && Range.isCollapsed(selection)
  const link = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : []
  }

  if (isCollapsed) {
    Transforms.insertNodes(editor, link)
  } else {
    Transforms.wrapNodes(editor, link, { split: true })
    Transforms.collapse(editor, { edge: 'end' })
  }
}

const Menu = () => {
  const editor = useSlate()
  const active = isActive(editor)
  const { menu, setMenu } = useEditorState()
  const [form, setForm, handleChange] = useNodeMenuForm(editor, 'link', true)

  if (menu !== 'link') return false

  const handleClose = () => {
    if (form.url) {
      wrapLink(editor, form.url)
    } else {
      unwrapLink(editor)
    }
  }

  const handleRemove =
    active &&
    (() => {
      unwrapLink(editor)
    })

  return (
    <EditorMenuFormWrapper title="Configuração do link" onClose={handleClose} onRemove={handleRemove}>
      <Grid gap={0.5}>
        <Col flex={1}>
          <Input
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleClose()
                setMenu()
              }
            }}
            placeholder="Url"
            size="xsmall"
            value={form.url}
            onChange={value => handleChange('url', value)}
          />
        </Col>

        <Col flex={1}></Col>
      </Grid>
    </EditorMenuFormWrapper>
  )
}

const extend = editor => {
  const { insertData, insertText, isInline } = editor

  editor.isInline = element => ['link'].includes(element.type) || isInline(element)

  return editor
}

const LinkPlugin = {
  extend,
  isActive,
  toggle,
  ToggleIcon,
  Menu,
  Element: PluginElement
}

export default LinkPlugin
